import { useState, useEffect } from 'react'
import { X, Download, Search, Calendar, Loader } from 'lucide-react'
import { useStore } from '../store/useStore'
import { listUserSketches, loadFromP5js } from '../services/p5sync'
import { showToast } from './ui/Toast'

export default function SketchBrowser({ onClose }) {
  const [sketches, setSketches] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingSketch, setLoadingSketch] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const { user, setFiles, setSketchInfo, setCurrentFile } = useStore()

  useEffect(() => {
    loadSketches()
  }, [])

  const loadSketches = async () => {
    try {
      setLoading(true)
      const userSketches = await listUserSketches()
      setSketches(userSketches)
    } catch (error) {
      showToast('Failed to load sketches: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleLoadSketch = async (sketch) => {
    try {
      setLoadingSketch(sketch.id)
      const result = await loadFromP5js(sketch.id)

      if (result.success) {
        // Convert loaded files to our format
        const loadedFiles = result.sketch.files.map(f => ({
          name: f.name,
          content: f.content,
          type: f.type || f.name.split('.').pop()
        }))

        setFiles(loadedFiles)
        setSketchInfo({
          id: result.sketch.id,
          name: result.sketch.name
        })
        
        // Set current file to sketch.js or first file
        const mainFile = loadedFiles.find(f => f.name === 'sketch.js') || loadedFiles[0]
        if (mainFile) {
          setCurrentFile(mainFile.name)
        }

        showToast(`Loaded: ${result.sketch.name}`, 'success')
        onClose()
      } else {
        showToast('Failed to load sketch: ' + result.error, 'error')
      }
    } catch (error) {
      showToast('Error loading sketch: ' + error.message, 'error')
    } finally {
      setLoadingSketch(null)
    }
  }

  const filteredSketches = sketches.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-ide-surface border border-ide-border rounded-xl max-w-3xl w-full h-[600px] flex flex-col relative shadow-modal animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ide-border">
          <div>
            <h2 className="text-xl font-semibold text-ide-text">Your Sketches</h2>
            <p className="text-ide-textMuted text-sm mt-1">
              Load sketches from editor.p5js.org
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-ide-textMuted hover:text-ide-text transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-ide-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ide-textDim" size={16} />
            <label htmlFor="sketch-search" className="sr-only">Search sketches</label>
            <input
              id="sketch-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search sketches..."
              className="w-full bg-ide-bg border border-ide-border rounded-lg pl-10 pr-4 py-2 text-sm text-ide-text placeholder-ide-textDim focus:outline-none focus:border-ide-borderFocus focus:ring-1 focus:ring-ide-accent/30"
            />
          </div>
        </div>

        {/* Sketch List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader className="animate-spin mx-auto mb-3 text-ide-accent" size={32} />
                <p className="text-ide-textMuted">Loading your sketches...</p>
              </div>
            </div>
          ) : filteredSketches.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-ide-textMuted">
                {searchTerm ? (
                  <>
                    <p>No sketches found matching "{searchTerm}"</p>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-ide-accent hover:underline mt-2"
                    >
                      Clear search
                    </button>
                  </>
                ) : (
                  <>
                    <p>No sketches found</p>
                    <p className="text-sm mt-2">
                      Create one at{' '}
                      <a
                        href="https://editor.p5js.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-ide-accent hover:underline"
                      >
                        editor.p5js.org
                      </a>
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredSketches.map(sketch => (
                <div
                  key={sketch.id}
                  className="bg-ide-bg border border-ide-border rounded-lg p-4 hover:border-ide-borderHover transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-ide-text truncate mb-1">
                        {sketch.name}
                      </h3>
                      <div className="flex items-center gap-4 text-xs text-ide-textMuted">
                        <div className="flex items-center gap-1">
                          <Calendar size={12} />
                          <span>Updated {formatDate(sketch.updatedAt)}</span>
                        </div>
                        {sketch.createdAt && (
                          <span>Created {formatDate(sketch.createdAt)}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleLoadSketch(sketch)}
                      disabled={loadingSketch === sketch.id}
                      className="flex items-center gap-2 bg-ide-accent hover:bg-ide-accentHover text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
                    >
                      {loadingSketch === sketch.id ? (
                        <>
                          <Loader className="animate-spin" size={14} />
                          <span>Loading...</span>
                        </>
                      ) : (
                        <>
                          <Download size={14} />
                          <span>Load</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-ide-border flex items-center justify-between">
          <p className="text-sm text-ide-textMuted">
            {filteredSketches.length} sketch{filteredSketches.length !== 1 ? 'es' : ''} found
          </p>
          <button
            onClick={loadSketches}
            className="text-ide-accent hover:underline text-sm"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  )
}
